// Cadmus — Kraftwerk Loop / R. Hütter, Kling Klang Werk 2024
// 15 seconden loop, 114 BPM, 32 steps, D minor

export class CadmusLoop {
  constructor(bpm = 114) {
    this.BPM = bpm;
    this.STEPS = 32;
    this.ctx = null;
    this.master = null;
    this.isPlaying = false;
    this.currentStep = -1;
    this.nextStepTime = 0;
    this.scheduleId = null;
    this.scheduledSteps = [];
    this.onStep = null;

    this.PATTERNS = {
      kick:  [1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0, 0,1,0,0],
      snare: [0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0, 1,0,0,1],
      hihat: [1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0, 1,0,1,0],
      bass:  [1,0,0,0, 0,0,1,0, 0,0,0,0, 1,0,0,0, 1,0,0,0, 0,0,1,0, 0,0,0,0, 0,0,0,0],
      lead:  [0,0,0,0, 0,0,0,0, 1,0,0,1, 0,0,1,0, 0,0,0,0, 0,0,0,0, 1,0,1,0, 0,0,1,0],
      chord: [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 1,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0],
      sweep: [1,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0],
      ping:  [0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,0, 0,0,0,1],
    };

    this.LEAD_NOTES = [
      0,0,0,0, 0,0,0,0,
      293.66,0,0,349.23, 0,0,440,0,
      0,0,0,0, 0,0,0,0,
      293.66,0,349.23,0, 0,0,587.33,0
    ];
    this.BASS_NOTES = [
      146.83,0,0,0, 0,0,73.42,0,
      0,0,0,87.31,  0,0,0,0,
      146.83,0,0,0, 0,0,73.42,0,
      0,0,0,0,      0,0,0,0
    ];
  }

  setBpm(bpm) { this.BPM = bpm; }

  start() {
    if (this.isPlaying) this.stop();
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (this.ctx.state === 'suspended') this.ctx.resume();
    this._buildMaster();
    this.isPlaying = true;
    this.currentStep = -1;
    this.nextStepTime = this.ctx.currentTime + 0.05;
    this._schedule();
  }

  stop() {
    this.isPlaying = false;
    if (this.scheduleId) clearTimeout(this.scheduleId);
    this.scheduleId = null;
    this.scheduledSteps = [];
    if (this.ctx) {
      this.ctx.close();
      this.ctx = null;
    }
  }

  _buildMaster() {
    const ctx = this.ctx;
    const conv = ctx.createConvolver();
    const len = Math.floor(ctx.sampleRate * 1.2);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++)
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.0);
    }
    conv.buffer = buf;
    this.master = ctx.createGain();
    this.master.gain.value = 0.72;
    const wet = ctx.createGain(); wet.gain.value = 0.22;
    const dry = ctx.createGain(); dry.gain.value = 1.0;
    this.master.connect(dry);  dry.connect(ctx.destination);
    this.master.connect(conv); conv.connect(wet); wet.connect(ctx.destination);
  }

  _schedule() {
    if (!this.isPlaying) return;
    const stepDur = (60 / this.BPM) / 2;
    while (this.nextStepTime < this.ctx.currentTime + 0.18) {
      const step = (this.currentStep + 1) % this.STEPS;
      this.currentStep = step;
      this._triggerStep(step, this.nextStepTime);
      this.scheduledSteps.push({ step, time: this.nextStepTime });
      this.nextStepTime += stepDur;
    }
    this.scheduledSteps = this.scheduledSteps.filter(s => s.time > this.ctx.currentTime - 0.1);
    if (this.onStep) {
      const now = this.ctx.currentTime;
      const cur = this.scheduledSteps.reduce((best, s) =>
        Math.abs(s.time - now) < Math.abs(best.time - now) ? s : best,
        { step: -1, time: Infinity }
      );
      if (cur.step >= 0) this.onStep(cur.step);
    }
    this.scheduleId = setTimeout(() => this._schedule(), 30);
  }

  _triggerStep(step, t0) {
    const p = this.PATTERNS;
    if (p.kick[step])  this._kick(t0);
    if (p.snare[step]) this._snare(t0);
    if (p.hihat[step]) this._hihat(t0);
    if (p.bass[step]  && this.BASS_NOTES[step]) this._bass(t0, this.BASS_NOTES[step]);
    if (p.lead[step]  && this.LEAD_NOTES[step]) this._lead(t0, this.LEAD_NOTES[step]);
    if (p.chord[step]) this._chord(t0);
    if (p.sweep[step]) this._sweep(t0);
    if (p.ping[step])  this._ping(t0);
  }

  _osc(type, freq, t0, dur, amp, cutoff, q = 2) {
    const o = this.ctx.createOscillator();
    const f = this.ctx.createBiquadFilter();
    const g = this.ctx.createGain();
    o.type = type; o.frequency.value = freq;
    f.type = 'lowpass'; f.frequency.value = cutoff; f.Q.value = q;
    g.gain.setValueAtTime(amp, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(f); f.connect(g); g.connect(this.master);
    o.start(t0); o.stop(t0 + dur + 0.05);
  }

  _sine(freq, t0, dur, amp, cutoff) {
    this._osc('sine', freq, t0, dur, amp, cutoff || 400);
  }

  _kick(t0) {
    const o = this.ctx.createOscillator(), g = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(110, t0);
    o.frequency.exponentialRampToValueAtTime(35, t0 + 0.18);
    g.gain.setValueAtTime(0.55, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
    o.connect(g); g.connect(this.master);
    o.start(t0); o.stop(t0 + 0.25);
  }

  _snare(t0) {
    const bd = this.ctx.createOscillator(), bdG = this.ctx.createGain();
    bd.type = 'sine';
    bd.frequency.setValueAtTime(320, t0);
    bd.frequency.exponentialRampToValueAtTime(80, t0 + 0.05);
    bdG.gain.setValueAtTime(0.38, t0);
    bdG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.055);
    bd.connect(bdG); bdG.connect(this.master);
    bd.start(t0); bd.stop(t0 + 0.07);

    const nLen = Math.ceil(this.ctx.sampleRate * 0.085);
    const nBuf = this.ctx.createBuffer(1, nLen, this.ctx.sampleRate);
    const nd = nBuf.getChannelData(0);
    for (let j = 0; j < nLen; j++) nd[j] = Math.random() * 2 - 1;
    const ns = this.ctx.createBufferSource();
    const nHi = this.ctx.createBiquadFilter();
    const nBp = this.ctx.createBiquadFilter();
    const nG = this.ctx.createGain();
    ns.buffer = nBuf;
    nHi.type = 'highpass'; nHi.frequency.value = 2000;
    nBp.type = 'bandpass'; nBp.frequency.value = 4600; nBp.Q.value = 1.5;
    nG.gain.setValueAtTime(0.50, t0);
    nG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.075);
    ns.connect(nHi); nHi.connect(nBp); nBp.connect(nG); nG.connect(this.master);
    ns.start(t0); ns.stop(t0 + 0.09);

    const tl = this.ctx.createOscillator(), tG = this.ctx.createGain(), tF = this.ctx.createBiquadFilter();
    tl.type = 'sine'; tl.frequency.value = 900;
    tF.type = 'highpass'; tF.frequency.value = 540;
    tG.gain.setValueAtTime(0.09, t0 + 0.008);
    tG.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.11);
    tl.connect(tF); tF.connect(tG); tG.connect(this.master);
    tl.start(t0 + 0.006); tl.stop(t0 + 0.13);
  }

  _hihat(t0) {
    const nLen = Math.ceil(this.ctx.sampleRate * 0.045);
    const nBuf = this.ctx.createBuffer(1, nLen, this.ctx.sampleRate);
    const nd = nBuf.getChannelData(0);
    for (let j = 0; j < nLen; j++) nd[j] = Math.random() * 2 - 1;
    const ns = this.ctx.createBufferSource();
    const f1 = this.ctx.createBiquadFilter();
    const f2 = this.ctx.createBiquadFilter();
    const g  = this.ctx.createGain();
    ns.buffer = nBuf;
    f1.type = 'highpass'; f1.frequency.value = 7000;
    f2.type = 'bandpass'; f2.frequency.value = 9000; f2.Q.value = 0.8;
    g.gain.setValueAtTime(0.18, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.032);
    ns.connect(f1); f1.connect(f2); f2.connect(g); g.connect(this.master);
    ns.start(t0); ns.stop(t0 + 0.04);
  }

  _bass(t0, freq) {
    this._osc('square', freq,       t0, 0.22, 0.42, 300, 2);
    this._sine(freq * 0.5,          t0, 0.30, 0.35, 160);
    this._sine(freq * 0.25,         t0, 0.38, 0.18, 90);
  }

  _lead(t0, freq) {
    this._osc('square', freq,       t0, 0.14, 0.30, 680, 2.5);
    this._sine(freq * 0.5,          t0, 0.20, 0.18, 200);
    const ck = this.ctx.createOscillator(), cg = this.ctx.createGain();
    ck.type = 'square'; ck.frequency.value = freq * 2.5;
    cg.gain.setValueAtTime(0.14, t0);
    cg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.012);
    ck.connect(cg); cg.connect(this.master);
    ck.start(t0); ck.stop(t0 + 0.016);
  }

  _chord(t0) {
    [293.66, 349.23, 440.00].forEach((freq, i) => {
      this._osc('sawtooth', freq,         t0 + i * 0.016, 0.55, 0.12, 700 + i * 60);
      this._osc('sawtooth', freq * 1.007, t0 + i * 0.016, 0.55, 0.06, 700);
      this._sine(freq * 0.5, t0, 0.65, 0.08, 180);
    });
    this._sine(146.83, t0, 0.70, 0.28, 160);
    this._sine(73.42,  t0, 0.80, 0.16, 90);
  }

  _sweep(t0) {
    const o = this.ctx.createOscillator(), g = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(220, t0);
    o.frequency.exponentialRampToValueAtTime(1100, t0 + 0.22);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.18, t0 + 0.04);
    g.gain.setValueAtTime(0.18, t0 + 0.16);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.25);
    o.connect(g); g.connect(this.master);
    o.start(t0); o.stop(t0 + 0.28);
  }

  _ping(t0) {
    const o = this.ctx.createOscillator(), g = this.ctx.createGain();
    o.type = 'sine'; o.frequency.value = 1320;
    g.gain.setValueAtTime(0.24, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
    o.connect(g); g.connect(this.master);
    o.start(t0); o.stop(t0 + 0.42);
  }
}
